/**
 * Picture Talk — gateway `/v1/learning/picture-talk/*` (student scenes + detail).
 */
import { createHttpClient } from '@/api/http';
import { serviceUrls } from '@/config/serviceUrls';
import { isAxiosError } from 'axios';
import { LearningHttpError } from './stories';

const client = createHttpClient(serviceUrls.learning);

/** Normalized 0–1 bbox: top-left + size in scene image space. */
export interface PictureTalkBboxNorm {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PictureTalkSceneListItem {
  scene_id: string;
  scene_title_ta?: string;
  title?: string;
  scene_title_en?: string;
  /** Optional richer scene copy when gateway provides it. */
  scene_summary_ta?: string;
  scene_summary_en?: string;
  scene_theme?: string;
  primary_image_url?: string | null;
}

export interface PictureTalkSceneItem {
  id: string;
  bbox: PictureTalkBboxNorm;
  /** Normalized vertices (0–1); reserved for future overlay — not used for hit-testing in phase 1. */
  polygon_norm?: [number, number][] | null;
  item_title_ta?: string;
  title?: string;
  item_title_en?: string;
  description_ta?: string;
  description?: string;
  description_en?: string;
  image_url?: string | null;
}

export interface PictureTalkSceneDetail extends PictureTalkSceneListItem {
  width?: number;
  height?: number;
  items: PictureTalkSceneItem[];
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function readSceneId(raw: Record<string, unknown>): string | null {
  const a = raw.scene_id ?? raw.sceneId ?? raw.id ?? raw._id;
  return typeof a === 'string' && a.trim() ? a.trim() : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** Scene hero image: matches gateway fields + legacy names. */
function pickScenePrimaryImageUrl(r: Record<string, unknown>): string | null {
  const direct =
    str(r.derived_page_image_url) ||
    str(r.primary_image_url) ||
    str(r.primaryImageUrl) ||
    str(r.image_url) ||
    null;
  if (direct) return direct;
  const urls = r.derived_page_image_urls;
  if (Array.isArray(urls)) {
    for (const x of urls) {
      const s = str(x);
      if (s) return s;
    }
  }
  return null;
}

/**
 * Detail GET may return a flat scene object, or wrapped shapes such as:
 * `{ ok, scene, items }`, `{ scene, items }`, `{ data: { scene, items } }`.
 * Items may live on the wrapper (sibling of `scene`) or on `scene.items`.
 */
function unwrapSceneDetailPayload(data: unknown): {
  scene: Record<string, unknown>;
  items: unknown[];
} | null {
  if (!data || typeof data !== 'object') return null;
  let root = data as Record<string, unknown>;

  if (root.data && typeof root.data === 'object' && !Array.isArray(root.data)) {
    root = root.data as Record<string, unknown>;
  }

  const nested = root.scene;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const scene = nested as Record<string, unknown>;
    const fromRoot = Array.isArray(root.items) ? root.items : [];
    const fromScene = Array.isArray(scene.items) ? scene.items : [];
    const items = fromRoot.length > 0 ? fromRoot : fromScene;
    return { scene, items };
  }

  const items = Array.isArray(root.items) ? root.items : [];
  return { scene: root, items };
}

/** Accepts array, `{ items }`, `{ scenes }`, `{ data }`, `{ results }`, or `{ data: { scenes } }`. */
export function normalizePictureTalkSceneList(data: unknown): PictureTalkSceneListItem[] {
  let root: unknown = data;
  if (root && typeof root === 'object') {
    const o = root as Record<string, unknown>;
    if (o.data && typeof o.data === 'object' && !Array.isArray(o.data)) {
      root = o.data;
    }
  }

  let arr: unknown[] = [];
  if (Array.isArray(root)) arr = root;
  else if (root && typeof root === 'object') {
    const o = root as Record<string, unknown>;
    if (Array.isArray(o.items)) arr = o.items;
    else if (Array.isArray(o.scenes)) arr = o.scenes;
    else if (Array.isArray(o.data)) arr = o.data;
    else if (Array.isArray(o.results)) arr = o.results;
  }

  const out: PictureTalkSceneListItem[] = [];
  for (const row of arr) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const scene_id = readSceneId(r);
    if (!scene_id) continue;
    const primary = pickScenePrimaryImageUrl(r);
    out.push({
      scene_id,
      scene_title_ta: typeof r.scene_title_ta === 'string' ? r.scene_title_ta : undefined,
      title: typeof r.title === 'string' ? r.title : undefined,
      scene_title_en: typeof r.scene_title_en === 'string' ? r.scene_title_en : undefined,
      scene_summary_ta: str(r.scene_summary_ta ?? r.summary_ta) ?? undefined,
      scene_summary_en: str(r.scene_summary_en ?? r.summary_en) ?? undefined,
      scene_theme: str(r.scene_theme ?? r.theme) ?? undefined,
      primary_image_url: primary,
    });
  }
  return out;
}

export function parseBboxNorm(v: unknown): PictureTalkBboxNorm | null {
  if (Array.isArray(v) && v.length >= 4) {
    const x = num(v[0]);
    const y = num(v[1]);
    const w = num(v[2]);
    const h = num(v[3]);
    if (x != null && y != null && w != null && h != null && w > 0 && h > 0) {
      return { x, y, w, h };
    }
    return null;
  }
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const x = num(o.x ?? o.left);
    const y = num(o.y ?? o.top);
    const w = num(o.w ?? o.width);
    const h = num(o.h ?? o.height);
    if (x != null && y != null && w != null && h != null && w > 0 && h > 0) {
      return { x, y, w, h };
    }
  }
  return null;
}

function readItemId(raw: Record<string, unknown>, index: number): string {
  const a = raw.item_id ?? raw.id ?? raw._id;
  if (typeof a === 'string' && a.trim()) return a.trim();
  return `item-${index}`;
}

function parsePolygonNorm(v: unknown): [number, number][] | null {
  if (!Array.isArray(v) || v.length < 3) return null;
  const pts: [number, number][] = [];
  for (const p of v) {
    if (Array.isArray(p) && p.length >= 2) {
      const x = num(p[0]);
      const y = num(p[1]);
      if (x != null && y != null) pts.push([x, y]);
    } else if (p && typeof p === 'object') {
      const o = p as Record<string, unknown>;
      const x = num(o.x ?? o[0]);
      const y = num(o.y ?? o[1]);
      if (x != null && y != null) pts.push([x, y]);
    }
  }
  return pts.length >= 3 ? pts : null;
}

function normalizeSceneDetail(data: unknown): PictureTalkSceneDetail | null {
  const unwrapped = unwrapSceneDetailPayload(data);
  if (!unwrapped) return null;
  const r = unwrapped.scene;
  const scene_id = readSceneId(r);
  if (!scene_id) return null;

  const width = num(r.width ?? r.image_width);
  const height = num(r.height ?? r.image_height);
  const primary = pickScenePrimaryImageUrl(r);

  const items: PictureTalkSceneItem[] = [];
  unwrapped.items.forEach((row, index) => {
    if (!row || typeof row !== 'object') return;
    const o = row as Record<string, unknown>;
    const bbox = parseBboxNorm(o.bbox_norm ?? o.bboxNorm ?? o.bbox);
    if (!bbox) return;
    const poly = parsePolygonNorm(o.polygon_norm ?? o.polygonNorm);
    const img =
      str(o.source_page_image_url) ||
      str(o.sourcePageImageUrl) ||
      str(o.image_url) ||
      str(o.item_image_url) ||
      null;
    const labelTa = str(o.label_ta) ?? str(o.labelTa);
    const labelEn = str(o.label_en) ?? str(o.labelEn);
    items.push({
      id: readItemId(o, index),
      bbox,
      polygon_norm: poly ?? undefined,
      item_title_ta: str(o.item_title_ta) ?? labelTa ?? undefined,
      title: str(o.title) ?? undefined,
      item_title_en: str(o.item_title_en) ?? labelEn ?? undefined,
      description_ta: str(o.description_ta) ?? undefined,
      description: str(o.description) ?? undefined,
      description_en: str(o.description_en) ?? undefined,
      image_url: img,
    });
  });

  return {
    scene_id,
    scene_title_ta: str(r.scene_title_ta) ?? undefined,
    title: str(r.title) ?? undefined,
    scene_title_en: str(r.scene_title_en) ?? undefined,
    scene_summary_ta: str(r.scene_summary_ta ?? r.summary_ta) ?? undefined,
    scene_summary_en: str(r.scene_summary_en ?? r.summary_en) ?? undefined,
    scene_theme: str(r.scene_theme ?? r.theme) ?? undefined,
    primary_image_url: primary,
    width: width ?? undefined,
    height: height ?? undefined,
    items,
  };
}

function toLearningHttpError(e: unknown, fallbackMessage: string): LearningHttpError {
  if (isAxiosError(e) && e.response) {
    const data = e.response.data;
    const bodyText = typeof data === 'string' ? data : JSON.stringify(data ?? {});
    return new LearningHttpError(
      `${fallbackMessage}: ${e.response.status}`,
      e.response.status,
      bodyText
    );
  }
  if (e instanceof Error) return new LearningHttpError(e.message, 0, '');
  return new LearningHttpError(fallbackMessage, 0, '');
}

export interface PictureTalkSceneListParams {
  grade?: number;
  book_id?: string;
  lesson_no?: number;
  unit_no?: number;
  scene_mode?: string;
  skip?: number;
  limit?: number;
}

function cleanParams(params: PictureTalkSceneListParams): Record<string, number | string> {
  const out: Record<string, number | string> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (typeof value === 'number' && !Number.isFinite(value)) return;
    out[key] = value as number | string;
  });
  return out;
}

export async function listPictureTalkScenes(
  params: PictureTalkSceneListParams = {}
): Promise<PictureTalkSceneListItem[]> {
  try {
    const res = await client.get<unknown>('/v1/learning/picture-talk/scenes', {
      params: cleanParams(params),
    });
    return normalizePictureTalkSceneList(res.data);
  } catch (e) {
    throw toLearningHttpError(e, 'Picture Talk list failed');
  }
}

export async function getPictureTalkScene(sceneId: string): Promise<PictureTalkSceneDetail> {
  const id = encodeURIComponent(sceneId);
  try {
    const res = await client.get<unknown>(`/v1/learning/picture-talk/scenes/${id}`);
    const detail = normalizeSceneDetail(res.data);
    if (!detail) {
      throw new LearningHttpError('Invalid scene response', 500, JSON.stringify(res.data));
    }
    return detail;
  } catch (e) {
    if (e instanceof LearningHttpError) throw e;
    throw toLearningHttpError(e, 'Picture Talk scene failed');
  }
}

/** Tamil-first scene title for student UI. */
export function displaySceneTitle(s: Pick<PictureTalkSceneListItem, 'scene_title_ta' | 'title' | 'scene_title_en' | 'scene_id'>): string {
  return (
    s.scene_title_ta?.trim() ||
    s.title?.trim() ||
    s.scene_title_en?.trim() ||
    s.scene_id ||
    'Scene'
  );
}

/** Tamil-first item title. */
export function displayItemTitle(it: Pick<PictureTalkSceneItem, 'item_title_ta' | 'title' | 'item_title_en' | 'id'>): string {
  return it.item_title_ta?.trim() || it.title?.trim() || it.item_title_en?.trim() || it.id;
}

/** Tamil-first body: Tamil block(s) then English when both exist. */
export function displayItemDescription(it: PictureTalkSceneItem): string {
  const ta = [it.description_ta, it.description].map((x) => x?.trim()).filter(Boolean) as string[];
  const en = [it.description_en].map((x) => x?.trim()).filter(Boolean) as string[];
  const parts: string[] = [];
  if (ta.length) parts.push(ta.join('\n\n'));
  if (en.length) parts.push(en.join('\n\n'));
  return parts.join('\n\n') || '';
}

/** English-leaning scene title for START payloads (not Tamil-first). */
export function displaySceneTitleEnglish(s: Pick<PictureTalkSceneListItem, 'scene_title_en' | 'title' | 'scene_title_ta' | 'scene_id'>): string {
  return s.scene_title_en?.trim() || s.title?.trim() || s.scene_title_ta?.trim() || s.scene_id || '';
}

export function displaySceneSummaryTa(s: PictureTalkSceneDetail): string | undefined {
  return s.scene_summary_ta?.trim() || undefined;
}

export function displaySceneSummaryEn(s: PictureTalkSceneDetail): string | undefined {
  return s.scene_summary_en?.trim() || undefined;
}

export function displaySceneTheme(s: PictureTalkSceneDetail): string | undefined {
  return s.scene_theme?.trim() || undefined;
}

/** English-leaning item label for EVENT lines. */
export function displayItemEnglishTitle(it: PictureTalkSceneItem): string {
  return it.item_title_en?.trim() || it.title?.trim() || it.item_title_ta?.trim() || it.id;
}

/** Comma-separated Tamil-first item labels for START / OBJECT_CLICKED. */
export function buildItemsTaList(scene: PictureTalkSceneDetail): string {
  return scene.items.map((i) => displayItemTitle(i)).join(', ');
}
